/**
 * Which section of the branding sheet carries its upsell, React-free by mandate (epic #26).
 *
 * The sheet stacks editors that are each gated on their own capability — `customerLogoAndColors` for the
 * brand and the download-page theme, `customDomain` for the claimed address (ui#529). Every one of them
 * is Premium and every one of them renders the SAME "Available on Premium." sentence, so letting each
 * section answer for itself put that sentence on screen twice for a FREE sender. That is not only noise:
 * it is the exact strict-mode violation that broke both customer-branding specs when the theme section
 * shipped its own copy of the note (8b6b8ce4), because two elements resolve one accessible string.
 *
 * So the sheet renders ONE note and this function says whose it is. The rule is "the first section the
 * sender cannot use, and only that one", which holds the invariant at ≤ 1 note for every combination
 * while still telling a partially-entitled sender which specific thing needs the upgrade.
 *
 * Deliberately NOT collapsed to a single sheet-wide `entitled` boolean. The two capabilities are separate
 * flags on `/plan` that happen to move together today; a sheet-wide gate would silently lock an editor
 * the sender had paid for the moment they diverge.
 */

/** The section that owns the sheet's single upsell, or `"none"` when nothing is locked. */
export type BrandSheetUpsell = "none" | "brand" | "subdomain";

/** The sheet's capability flags, already resolved from the sender's plan by the caller. */
export interface BrandSheetEntitlements {
	/** `customerLogoAndColors` — the logo/accent editor and the download-page theme. */
	brandEntitled: boolean;
	/** `customDomain` — the claimed-address editor. */
	subdomainEntitled: boolean;
}

export function brandSheetUpsell({brandEntitled, subdomainEntitled}: BrandSheetEntitlements): BrandSheetUpsell {
	if (!brandEntitled) {
		return "brand";
	}
	return subdomainEntitled ? "none" : "subdomain";
}
